import { getReportTlfSelection } from "./getReportTlfSelection";
import { hierarchyToNodes } from "./hierarchyToNodes";
import {
  ADD_HIERARCHY_NODES,
  ADD_SELECTION,
  IHierarchyBrowserAction,
  IHierarchyBrowserContext,
  REMOVE_SELECTION,
  SET_CORPORATE_HIERARCHY,
  SET_CURRENT_ACCESS_RIGHTS,
  SET_CURRENT_HIERARCHY,
  SET_NEW_REPORT_DETAILS,
  SET_NEW_TLF_ITEM_DETAILS,
  SET_NEW_TLF_ITEM_DETAILS_VALID,
  SET_SELECTIONS,
  UPSERT_HIERARCHY_TLF_ITEM_INDEX,
} from "./interface";

/**
 * Reducer for the hierarchy browser state.
 *
 * @param state - Current hierarchy browser state
 * @param action - Action to apply to the state
 * @returns The updated hierarchy browser state
 */
export const hierarchyBrowserReducer = (
  state: IHierarchyBrowserContext,
  action: IHierarchyBrowserAction,
): IHierarchyBrowserContext => {
  switch (action.operation) {
    case ADD_HIERARCHY_NODES: {
      const newTreeList = [
        ...state.treeList.filter((hn) => !action.treeListData.find((h) => h.id === hn.id)),
        ...action.treeListData,
      ].sort((a, b) => a.label.localeCompare(b.label));
      return { ...state, treeList: newTreeList };
    }
    case ADD_SELECTION: {
      const newSelections = [
        ...state.selections.filter((s) => s.id !== action.selection.id),
        action.selection,
      ].sort((a, b) => a.id.localeCompare(b.id));
      return { ...state, selections: newSelections };
    }
    case REMOVE_SELECTION: {
      const newSelections = state.selections.filter((s) => s.id !== action.selection.id);
      return { ...state, selections: newSelections };
    }
    case SET_CORPORATE_HIERARCHY: {
      const treeList = hierarchyToNodes([action.hierarchyNode]);
      return {
        ...state,
        root: action.hierarchyNode.id,
        treeList,
        currentHierarchy: action.hierarchyNode.id,
        currentHierarchyType: action.hierarchyNode.type,
      };
    }
    case SET_CURRENT_ACCESS_RIGHTS: {
      return { ...state, currentAccessRights: action.accessRights };
    }
    case SET_CURRENT_HIERARCHY: {
      const data = state.treeList.find((h) => h.id === action.nodeId)?.data;
      return {
        ...state,
        currentHierarchy: action.nodeId,
        currentHierarchyType: data ? data.type : state.currentHierarchyType,
      };
    }
    case SET_NEW_REPORT_DETAILS: {
      return {
        ...state,
        newReportDescription: action.newReportDescription,
        newReportName: action.newReportName,
        newReportType: action.newReportType,
        newReportValid: action.newReportValid,
      };
    }
    case SET_NEW_TLF_ITEM_DETAILS: {
      return { ...state, newTlfItemDetails: action.tlfItemDetails };
    }
    case SET_NEW_TLF_ITEM_DETAILS_VALID: {
      return { ...state, newTlfItemDetailsValid: action.tlfItemDetailsValid };
    }
    case SET_SELECTIONS: {
      return { ...state, selections: action.selections };
    }
    case UPSERT_HIERARCHY_TLF_ITEM_INDEX: {
      const previousItems =
        state.hierarchyTlfItemIndex.find((item) => item.hierarchyId === action.nodeId)
          ?.tlfItemIndex ?? [];
      const selections = state.selections.flatMap((selection) => {
        const item = action.tlfItemIndex.find((source) => source.id === selection.id);
        if (item) {
          return getReportTlfSelection(
            item,
            selection.includeMaster ?? selection.variantNames === undefined,
            selection.variantNames ?? [],
          ).map((updated) => ({ ...selection, ...updated }));
        }
        return previousItems.some((source) => source.id === selection.id) ? [] : [selection];
      });
      const index = state.hierarchyTlfItemIndex.findIndex((i) => i.hierarchyId === action.nodeId);
      if (index !== -1) {
        const newHierarchyTlfItemIndex = state.hierarchyTlfItemIndex.map((item, idx) =>
          idx === index ? { ...item, tlfItemIndex: action.tlfItemIndex } : item,
        );
        return { ...state, hierarchyTlfItemIndex: newHierarchyTlfItemIndex, selections };
      } else {
        const newHierarchyTlfItemIndex = [
          ...state.hierarchyTlfItemIndex,
          { hierarchyId: action.nodeId, tlfItemIndex: action.tlfItemIndex },
        ];
        return { ...state, hierarchyTlfItemIndex: newHierarchyTlfItemIndex, selections };
      }
    }
  }
};
